import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  registerServiceWorker, 
  subscribeToPushNotifications, 
  unsubscribeFromPushNotifications,
  checkNotificationPermission 
} from '@/utils/notifications';
import { Bell, BellOff, Newspaper } from 'lucide-react';

const NotificationSettings = () => {
  const [preferences, setPreferences] = useState({
    high_confidence_tips: true,
    tip_results: true,
    subscription_updates: true,
    new_sports: false,
    daily_digest: true,
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPreferences();
    checkPushStatus();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      setPreferences((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const checkPushStatus = async () => {
    const perm = await checkNotificationPermission();
    setPermission(perm);
    setPushEnabled(perm === 'granted');
  };

  const handleTogglePush = async () => {
    setLoading(true);
    
    try {
      if (!pushEnabled) {
        await registerServiceWorker();
        const subscription = await subscribeToPushNotifications();
        
        if (subscription) {
          await api.post('/notifications/subscribe', {
            subscription: subscription.toJSON()
          });
          
          setPushEnabled(true);
          toast.success('Push-Benachrichtigungen aktiviert!');
        } else {
          toast.error('Push-Benachrichtigungen wurden abgelehnt');
        }
      } else {
        await unsubscribeFromPushNotifications();
        await api.post('/notifications/unsubscribe');
        
        setPushEnabled(false);
        toast.success('Push-Benachrichtigungen deaktiviert');
      }
    } catch (error) {
      toast.error('Fehler beim Aendern der Einstellungen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);

    try {
      await api.post('/notifications/preferences', newPreferences);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <Card className="bg-[#1E1E1E] border-white/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          {pushEnabled ? <Bell className="w-5 h-5 text-[#00BFA6]" /> : <BellOff className="w-5 h-5" />}
          Benachrichtigungen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Push Toggle */}
        <div className="flex items-center justify-between p-4 bg-[#121212] rounded-lg">
          <div>
            <div className="font-medium text-white">Push-Benachrichtigungen</div>
            <div className="text-sm text-gray-400 mt-1">
              Erhalte sofortige Updates ueber neue High-Confidence-Tips
            </div>
          </div>
          <Button
            onClick={handleTogglePush}
            disabled={loading}
            data-testid="push-toggle-btn"
            className={`${
              pushEnabled 
                ? 'bg-[#00BFA6] hover:bg-[#009E8A]' 
                : 'bg-white/10 hover:bg-white/20'
            } text-white font-bold`}
          >
            {loading ? 'Laedt...' : (pushEnabled ? 'Aktiviert' : 'Aktivieren')}
          </Button>
        </div>

        {/* Preferences */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-400">Was moechtest du erhalten?</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
              <Label htmlFor="high-conf" className="text-white cursor-pointer flex-1">
                <div className="font-medium">High-Confidence Tips (85%+)</div>
                <div className="text-sm text-gray-400">Sofortige Benachrichtigung bei neuen Top-Tips</div>
              </Label>
              <Switch
                id="high-conf"
                checked={preferences.high_confidence_tips}
                onCheckedChange={(checked) => handlePreferenceChange('high_confidence_tips', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
              <Label htmlFor="daily-digest" className="text-white cursor-pointer flex-1">
                <div className="font-medium flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-[#00BFA6]" />
                  Taeglicher Value-Bet Digest
                </div>
                <div className="text-sm text-gray-400">Jeden Morgen um 8:00 die besten Value-Bets des Tages</div>
              </Label>
              <Switch
                id="daily-digest"
                data-testid="daily-digest-toggle"
                checked={preferences.daily_digest}
                onCheckedChange={(checked) => handlePreferenceChange('daily_digest', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
              <Label htmlFor="results" className="text-white cursor-pointer flex-1">
                <div className="font-medium">Tip-Ergebnisse</div>
                <div className="text-sm text-gray-400">Benachrichtigung wenn deine Tips validiert wurden</div>
              </Label>
              <Switch
                id="results"
                checked={preferences.tip_results}
                onCheckedChange={(checked) => handlePreferenceChange('tip_results', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
              <Label htmlFor="subscription" className="text-white cursor-pointer flex-1">
                <div className="font-medium">Abo-Updates</div>
                <div className="text-sm text-gray-400">Erinnerungen zu Abo-Verlaengerung und Kontingent</div>
              </Label>
              <Switch
                id="subscription"
                checked={preferences.subscription_updates}
                onCheckedChange={(checked) => handlePreferenceChange('subscription_updates', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
              <Label htmlFor="new-sports" className="text-white cursor-pointer flex-1">
                <div className="font-medium">Neue Sportarten</div>
                <div className="text-sm text-gray-400">Info wenn neue Sportarten hinzugefuegt werden</div>
              </Label>
              <Switch
                id="new-sports"
                checked={preferences.new_sports}
                onCheckedChange={(checked) => handlePreferenceChange('new_sports', checked)}
              />
            </div>
          </div>
        </div>

        {permission === 'denied' && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-gray-300">
              Benachrichtigungen wurden blockiert. Bitte aktiviere sie in deinen Browser-Einstellungen.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
